import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Users, RefreshCw, Search, Clock, Phone,
  Calendar, ChevronDown, Eye, X as CloseIcon,
  AlertCircle, UserCheck, Send, BotOff,
} from 'lucide-react';
import { Card, Loading, Button } from '../../components/common';
import conversationsService from '../../services/api/conversations.service';
import useBot from '../../hooks/useBot';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const formatSafeDate = (dateString) => {
  try {
    if (!dateString) return 'Data não disponível';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Data inválida';
    return formatDistanceToNow(date, { addSuffix: true, locale: ptBR });
  } catch { return 'Data inválida'; }
};

// Configuração por tenant: especialistas e como detectar handoff
const BOT_CONFIG = {
  ebes_bacabal: {
    specialists: ['Zé Filho'],
    isHandedOff: (conv) => !!conv.context?.handoff_summary,
    getSpecialist: (conv) => conv.context?.handoff_summary ? 'Zé Filho' : null,
  },
};

const DEFAULT_CONFIG = {
  specialists: ['Silvia', 'Arimatea'],
  isHandedOff: (conv) => !!conv.context?.especialista,
  getSpecialist: (conv) => conv.context?.especialista || null,
};

const getBotConfig = (botId) => BOT_CONFIG[botId] || DEFAULT_CONFIG;

const isHandedOff = (conv, botId) => getBotConfig(botId).isHandedOff(conv);
const getSpecialist = (conv, botId) => getBotConfig(botId).getSpecialist(conv);

const SPECIALIST_STYLES = {
  Silvia: {
    card:       'bg-white dark:bg-slate-800/70 border-l-[3px] border-l-violet-400',
    cardHover:  'hover:bg-violet-50/40 dark:hover:bg-slate-800',
    avatarBg:   'bg-violet-50 dark:bg-violet-900/25',
    avatarText: 'text-violet-600 dark:text-violet-400',
    badge:      'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300',
  },
  Arimatea: {
    card:       'bg-white dark:bg-slate-800/70 border-l-[3px] border-l-orange-400',
    cardHover:  'hover:bg-orange-50/40 dark:hover:bg-slate-800',
    avatarBg:   'bg-orange-50 dark:bg-orange-900/25',
    avatarText: 'text-orange-600 dark:text-orange-400',
    badge:      'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  },
  'Zé Filho': {
    card:       'bg-white dark:bg-slate-800/70 border-l-[3px] border-l-emerald-400',
    cardHover:  'hover:bg-emerald-50/40 dark:hover:bg-slate-800',
    avatarBg:   'bg-emerald-50 dark:bg-emerald-900/25',
    avatarText: 'text-emerald-600 dark:text-emerald-400',
    badge:      'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
  },
};

const TRIAGE_STYLE = {
  card:       'bg-white dark:bg-slate-800/70 border-l-[3px] border-l-primary-400',
  cardHover:  'hover:bg-primary-50/40 dark:hover:bg-slate-800',
  avatarBg:   'bg-primary-50 dark:bg-primary-900/25',
  avatarText: 'text-primary-600 dark:text-primary-400',
  badge:      'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
};

const getConvStyle = (conv, botId) => {
  const specialist = getSpecialist(conv, botId);
  if (isHandedOff(conv, botId) && specialist) return SPECIALIST_STYLES[specialist] || TRIAGE_STYLE;
  return TRIAGE_STYLE;
};

const getClientName = (conv) =>
  conv.lead?.name || conv.context?.nome || conv.context?.name || 'Lead';

const getClientPhone = (conv) => conv.lead?.phone || conv.wa_id || '';

const ITEMS_PER_PAGE = 50;

const buildFilters = (specialists) => [
  { key: 'all',    label: 'Todas' },
  { key: 'triage', label: 'Em Triagem' },
  ...specialists.map((s) => ({ key: s, label: s })),
];

const buildMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    options.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
      fromDate: new Date(d.getFullYear(), d.getMonth(), 1),
      toDate:   new Date(d.getFullYear(), d.getMonth() + 1, 1),
    });
  }
  return options;
};

const MONTH_OPTIONS = buildMonthOptions();

const SalesConversations = () => {
  const { selectedBotId, selectedBot } = useBot();
  const botConfig = useMemo(() => getBotConfig(selectedBotId), [selectedBotId]);
  const FILTERS   = useMemo(() => buildFilters(botConfig.specialists), [botConfig]);

  const [loading, setLoading]                     = useState(true);
  const [conversations, setConversations]         = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [filter, setFilter]                       = useState('all');
  const [searchTerm, setSearchTerm]               = useState('');
  const [selectedMonth, setSelectedMonth]         = useState(MONTH_OPTIONS[0].value);
  const [refreshKey, setRefreshKey]               = useState(0);
  const [visibleCount, setVisibleCount]           = useState(ITEMS_PER_PAGE);
  const [error, setError]                         = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages]                   = useState([]);
  const [loadingMessages, setLoadingMessages]     = useState(false);

  // Composer state
  const [messageText, setMessageText]   = useState('');
  const [pauseBot, setPauseBot]         = useState(true);
  const [sending, setSending]           = useState(false);
  const [sendError, setSendError]       = useState(null);
  const [sendSuccess, setSendSuccess]   = useState(false);
  const messagesEndRef                  = useRef(null);

  useEffect(() => {
    if (!selectedBotId) return;
    let cancelled = false;
    const monthOpt = MONTH_OPTIONS.find((o) => o.value === selectedMonth) || MONTH_OPTIONS[0];
    setLoading(true);
    setConversations([]);
    setError(null);
    setVisibleCount(ITEMS_PER_PAGE);
    conversationsService
      .getAllConversations(selectedBotId, {
        status: 'all',
        fromDate: monthOpt.fromDate,
        toDate:   monthOpt.toDate,
      })
      .then((all) => { if (!cancelled) setConversations(all); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Erro ao carregar conversas'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBotId, selectedMonth, refreshKey]);

  useEffect(() => {
    let result = conversations;
    if (filter === 'triage') {
      result = conversations.filter((c) => !isHandedOff(c, selectedBotId));
    } else if (botConfig.specialists.includes(filter)) {
      result = conversations.filter(
        (c) => isHandedOff(c, selectedBotId) && getSpecialist(c, selectedBotId) === filter
      );
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          getClientName(c).toLowerCase().includes(s) ||
          getClientPhone(c).includes(s) ||
          (c.protocol || '').toLowerCase().includes(s)
      );
    }
    setFilteredConversations(result);
    setVisibleCount(ITEMS_PER_PAGE);
  }, [conversations, filter, searchTerm, selectedBotId, botConfig]);

  const loadConversations = () => setRefreshKey((k) => k + 1);

  const filterCounts = useMemo(() => {
    const counts = {
      all:    conversations.length,
      triage: conversations.filter((c) => !isHandedOff(c, selectedBotId)).length,
    };
    botConfig.specialists.forEach((s) => {
      counts[s] = conversations.filter(
        (c) => isHandedOff(c, selectedBotId) && getSpecialist(c, selectedBotId) === s
      ).length;
    });
    return counts;
  }, [conversations, selectedBotId, botConfig]);

  const visibleConversations = filteredConversations.slice(0, visibleCount);

  // Auto-scroll quando chegam novas mensagens
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Limpa composer ao fechar modal
  useEffect(() => {
    if (!selectedConversation) {
      setMessageText('');
      setSendError(null);
      setSendSuccess(false);
      setPauseBot(true);
    }
  }, [selectedConversation]);

  const handleViewMessages = async (conv) => {
    try {
      setLoadingMessages(true);
      setSelectedConversation(conv);
      setMessages([]);
      const data = await conversationsService.getMessages(selectedBotId, conv.id);
      setMessages(data.items || data.messages || []);
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      alert('Erro ao carregar mensagens: ' + err.message);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Limpa o número do telefone (remove sufixo @lid / @s.whatsapp.net etc.)
  const cleanPhone = (raw) => (raw || '').replace(/@[^\s]+$/, '').replace(/\D/g, '');

  const handleSendMessage = async () => {
    const text = messageText.trim();
    if (!text || !selectedConversation) return;

    const to = cleanPhone(getClientPhone(selectedConversation));
    if (!to) {
      setSendError('Número de telefone não encontrado para este lead.');
      return;
    }

    setSending(true);
    setSendError(null);
    setSendSuccess(false);

    // Adiciona mensagem otimisticamente na lista
    const optimistic = {
      id: `temp-${Date.now()}`,
      text,
      direction: 'outbound',
      fromMe: true,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setMessageText('');

    try {
      const result = await conversationsService.sendMessage(selectedBotId, {
        to,
        text,
        type: 'text',
        pauseBot,
      });

      if (!result?.ok) throw new Error('A API retornou ok: false');

      setSendSuccess(true);
      setTimeout(() => setSendSuccess(false), 3000);
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setSendError(err.message || 'Falha ao enviar. Tente novamente.');
      // Remove a mensagem otimista em caso de erro
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setMessageText(text); // restaura o texto
    } finally {
      setSending(false);
    }
  };

  const handleComposerKeyDown = (e) => {
    // Ctrl+Enter ou Cmd+Enter para enviar
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" text="Carregando conversas..." />
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2.5 font-display">
            <div className="w-8 h-8 bg-violet-50 dark:bg-violet-900/30 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            </div>
            {selectedBot?.name || 'Bot de Vendas'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 ml-10">
            Leads em atendimento e aguardando especialista
          </p>
        </div>
        <button
          onClick={loadConversations}
          disabled={loading}
          className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 self-start sm:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Legend */}
      <div className="flex gap-2 flex-wrap">
        {[
          { label: 'Em Triagem', dot: 'bg-primary-500', bg: 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800/40', text: 'text-primary-700 dark:text-primary-300' },
          ...botConfig.specialists.map((s) => {
            const style = SPECIALIST_STYLES[s] || {};
            const dotColor = style.avatarText?.replace('text-', 'bg-').replace('dark:text-', '').split(' ')[0] || 'bg-slate-400';
            const bgColor  = style.avatarBg?.split(' ')[0] || 'bg-slate-50';
            const border   = 'border-slate-200 dark:border-slate-700';
            const textColor = style.avatarText?.split(' ').join(' ') || 'text-slate-600';
            return { label: s, dot: dotColor, bg: `${bgColor} ${border}`, text: textColor };
          }),
        ].map(({ label, dot, bg, text }) => (
          <div key={label} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${bg} ${text}`}>
            <div className={`w-2 h-2 rounded-full ${dot}`} />
            {label}
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <button
              onClick={loadConversations}
              className="mt-2 text-xs font-semibold text-red-600 dark:text-red-400 hover:underline"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <Card padding="sm">
        <Card.Body className="p-3 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2.5">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por nome, telefone ou protocolo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400 focus:bg-white dark:focus:bg-slate-800 focus:outline-none transition-colors"
              />
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="pl-8 pr-7 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium focus:ring-2 focus:ring-primary-400/30 focus:outline-none appearance-none cursor-pointer transition-colors"
              >
                {MONTH_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1.5 flex-wrap">
            {FILTERS.map((f) => {
              const isActive = filter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                    isActive
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  {f.label}
                  <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                    isActive ? 'bg-white/20 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                  }`}>
                    {filterCounts[f.key] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      {/* List */}
      {filteredConversations.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
            <Users className="w-6 h-6 text-slate-300 dark:text-slate-600" />
          </div>
          <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Nenhuma conversa encontrada
          </h3>
          <p className="text-sm text-slate-400 dark:text-slate-500">
            {searchTerm ? 'Tente ajustar os filtros de busca' : 'Não há conversas nesta categoria'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleConversations.map((conv) => {
            const style     = getConvStyle(conv, selectedBotId);
            const specialist = getSpecialist(conv, selectedBotId);
            const handedOff  = isHandedOff(conv, selectedBotId);

            return (
              <div
                key={conv.id}
                className={`rounded-xl border border-slate-200 dark:border-slate-700/60 overflow-hidden shadow-sm ${style.card} ${style.cardHover} transition-colors`}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 ${style.avatarBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <Users className={`w-4.5 h-4.5 ${style.avatarText}`} style={{width:'18px',height:'18px'}} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate">
                            {getClientName(conv)}
                          </h3>

                          {handedOff && specialist ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${style.badge}`}>
                              <UserCheck className="w-3 h-3" />
                              {specialist}
                            </span>
                          ) : (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${style.badge}`}>
                              Em Triagem
                            </span>
                          )}

                          {conv.protocol && (
                            <span className="text-[11px] text-slate-400 dark:text-slate-500 font-mono">
                              #{conv.protocol}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate max-w-[180px]">{getClientPhone(conv)}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
                            <Calendar className="w-3 h-3 flex-shrink-0" />
                            <span>Iniciada {formatSafeDate(conv.createdAt)}</span>
                          </div>
                          {conv.lastInboundAt && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
                              <Clock className="w-3 h-3 flex-shrink-0" />
                              <span>Última msg {formatSafeDate(conv.lastInboundAt)}</span>
                            </div>
                          )}
                          {conv.context?.interesse_principal && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                              <span className="font-medium">Interesse:</span>
                              <span>{conv.context.interesse_principal}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleViewMessages(conv)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors flex-shrink-0"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Ver
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {visibleCount < filteredConversations.length && (
            <div className="flex justify-center pt-3">
              <button
                onClick={() => setVisibleCount((v) => v + ITEMS_PER_PAGE)}
                className="px-5 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                Carregar mais · {filteredConversations.length - visibleCount} restantes
              </button>
            </div>
          )}
        </div>
      )}

      {/* Messages Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700">

            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 ${getConvStyle(selectedConversation, selectedBotId).avatarBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Users className={`w-4 h-4 ${getConvStyle(selectedConversation, selectedBotId).avatarText}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                    {getClientName(selectedConversation)}
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 truncate">
                    {getClientPhone(selectedConversation)}
                  </p>
                </div>
                {isHandedOff(selectedConversation, selectedBotId) && getSpecialist(selectedConversation, selectedBotId) && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${getConvStyle(selectedConversation, selectedBotId).badge}`}>
                    <UserCheck className="w-3 h-3" />
                    {getSpecialist(selectedConversation, selectedBotId)}
                  </span>
                )}
              </div>
              <button
                onClick={() => setSelectedConversation(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0 ml-2"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Messages body */}
            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar bg-slate-50 dark:bg-slate-950">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-12">
                  <Loading size="md" text="Carregando mensagens..." />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm text-slate-400 dark:text-slate-500">Nenhuma mensagem encontrada</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((message, index) => {
                    const dir = message.direction || '';
                    const isOutbound =
                      dir === 'outbound' || dir === 'bot' || dir === 'agent' ||
                      dir === 'human' || dir === 'out' ||
                      message.fromMe === true || message.from_me === true;

                    let msgText = '[Mensagem sem texto]';
                    if (message.text && typeof message.text === 'string') {
                      msgText = message.text;
                    } else if (message.body) {
                      if (typeof message.body === 'string') {
                        msgText = message.body;
                      } else if (message.body.text) {
                        msgText = message.body.text;
                      } else if (typeof message.body === 'object') {
                        const typeMap = { image: '📷 Imagem', video: '🎥 Vídeo', audio: '🎵 Áudio', document: '📄 Documento' };
                        msgText = typeMap[message.body.type] || `[${message.body.type || 'desconhecido'}]`;
                      }
                    }

                    const isTemp = String(message.id).startsWith('temp-');

                    return (
                      <div key={message.id || index} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[72%] rounded-2xl px-4 py-2.5 transition-opacity ${
                          isOutbound
                            ? `bg-primary-500 text-white rounded-br-sm ${isTemp ? 'opacity-60' : 'opacity-100'}`
                            : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-bl-sm shadow-sm'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{msgText}</p>
                          <p className={`text-[11px] mt-1.5 ${isOutbound ? 'text-white/60' : 'text-slate-400 dark:text-slate-500'}`}>
                            {isTemp
                              ? 'Enviando…'
                              : new Date(message.timestamp || message.createdAt || message.created_at).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })
                            }
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {/* anchor para auto-scroll */}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="flex-shrink-0 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">

              {/* Feedback de erro/sucesso */}
              {sendError && (
                <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-lg">
                  <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-600 dark:text-red-400 flex-1">{sendError}</p>
                  <button onClick={() => setSendError(null)} className="text-red-400 hover:text-red-600">
                    <CloseIcon className="w-3 h-3" />
                  </button>
                </div>
              )}
              {sendSuccess && (
                <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 rounded-lg">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">Mensagem enviada com sucesso!</p>
                </div>
              )}

              {/* Input row */}
              <div className="flex gap-2 items-end">
                <div className="flex-1 relative">
                  <textarea
                    value={messageText}
                    onChange={(e) => {
                      setMessageText(e.target.value);
                      // Auto-resize
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }}
                    onKeyDown={handleComposerKeyDown}
                    placeholder="Digite uma mensagem… (Ctrl+Enter para enviar)"
                    rows={1}
                    disabled={sending}
                    className="
                      w-full resize-none rounded-xl px-4 py-2.5 text-sm
                      bg-slate-50 dark:bg-slate-800
                      border border-slate-200 dark:border-slate-700
                      text-slate-800 dark:text-slate-200
                      placeholder-slate-400 dark:placeholder-slate-500
                      focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-400
                      disabled:opacity-50
                      transition-colors leading-relaxed
                    "
                    style={{ minHeight: '42px', maxHeight: '120px' }}
                  />
                </div>

                <button
                  onClick={handleSendMessage}
                  disabled={!messageText.trim() || sending}
                  className="
                    flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0
                    bg-primary-500 hover:bg-primary-600
                    text-white
                    disabled:opacity-40 disabled:cursor-not-allowed
                    transition-colors shadow-sm
                  "
                  title="Enviar (Ctrl+Enter)"
                >
                  {sending
                    ? <RefreshCw className="w-4 h-4 animate-spin" />
                    : <Send className="w-4 h-4" />
                  }
                </button>
              </div>

              {/* Opções */}
              <div className="flex items-center justify-between mt-2.5">
                <label className="flex items-center gap-2 cursor-pointer group select-none">
                  <div
                    onClick={() => setPauseBot((v) => !v)}
                    className={`relative w-8 h-4 rounded-full transition-colors cursor-pointer ${
                      pauseBot ? 'bg-amber-400' : 'bg-slate-200 dark:bg-slate-700'
                    }`}
                  >
                    <div className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-transform ${
                      pauseBot ? 'translate-x-4' : 'translate-x-0'
                    }`} />
                  </div>
                  <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-300 transition-colors">
                    <BotOff className="w-3 h-3" />
                    Pausar bot ao enviar
                  </span>
                </label>
                <p className="text-[11px] text-slate-300 dark:text-slate-600">
                  {messageText.length > 0 ? `${messageText.length} caracteres` : 'Ctrl+Enter para enviar'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesConversations;
